/**
 * GDPR Cookie Consent Banner
 * Displays consent choices with glassmorphism design matching app aesthetic
 */

import React, { useState, useEffect } from 'react';
import { motion, AnimatePresence } from 'framer-motion';
import { CogIcon, ShieldCheckIcon } from '@heroicons/react/24/outline';
import { consentManager, ConsentPreferences } from '../../services/consentManager';

interface CookieConsentBannerProps {
  onConsentGiven?: (preferences: ConsentPreferences) => void;
}

export const CookieConsentBanner: React.FC<CookieConsentBannerProps> = ({
  onConsentGiven
}) => {
  const [isVisible, setIsVisible] = useState(false);
  const [showDetails, setShowDetails] = useState(false);
  const [isLoading, setIsLoading] = useState(false);
  
  // Granular consent state
  const [preferences, setPreferences] = useState({
    analytics: false,
    marketing: false,
    personalization: false
  });

  useEffect(() => {
    // Show banner if user hasn't consented
    const hasConsent = consentManager.hasConsent();
    setIsVisible(!hasConsent);
  }, []);

  const handleAcceptAll = async () => {
    setIsLoading(true);
    try {
      await consentManager.updateConsent({
        analytics: true,
        marketing: true,
        personalization: true
      }, 'banner');
      
      const fullPreferences = { necessary: true, analytics: true, marketing: true, personalization: true };
      onConsentGiven?.(fullPreferences);
      setIsVisible(false);
    } catch (error) {
      console.error('Error accepting all cookies:', error);
    } finally {
      setIsLoading(false);
    }
  };

  const handleRejectAll = async () => {
    setIsLoading(true);
    try {
      await consentManager.updateConsent({
        analytics: false,
        marketing: false,
        personalization: false
      }, 'banner');
      
      onConsentGiven?.({ necessary: true, analytics: false, marketing: false, personalization: false });
      setIsVisible(false);
    } catch (error) {
      console.error('Error rejecting cookies:', error);
    } finally {
      setIsLoading(false);
    }
  };

  const handleSavePreferences = async () => {
    setIsLoading(true);
    try {
      await consentManager.updateConsent(preferences, 'banner');
      
      const fullPreferences = { necessary: true, ...preferences };
      onConsentGiven?.(fullPreferences);
      setIsVisible(false);
    } catch (error) {
      console.error('Error saving preferences:', error);
    } finally {
      setIsLoading(false);
    }
  };

  const togglePreference = (key: keyof typeof preferences) => {
    setPreferences(prev => ({
      ...prev,
      [key]: !prev[key]
    }));
  };

  if (!isVisible) return null;

  return (
    <AnimatePresence>
      <motion.div
        initial={{ y: 100, opacity: 0 }}
        animate={{ y: 0, opacity: 1 }}
        exit={{ y: 100, opacity: 0 }}
        transition={{ type: "spring", damping: 25, stiffness: 200 }}
        className="fixed bottom-0 left-0 right-0 z-50 p-4 md:p-6"
      >
        <div className="max-w-6xl mx-auto">
          <div className="bg-white/10 backdrop-blur-lg border border-white/20 rounded-2xl shadow-2xl overflow-hidden">
            {/* Header */}
            <div className="p-6 md:p-8">
              <div className="flex items-start justify-between mb-6">
                <div className="flex items-center space-x-3">
                  <div className="p-2 bg-blue-500/10 rounded-lg">
                    <ShieldCheckIcon className="h-6 w-6 text-blue-400" />
                  </div>
                  <div>
                    <h3 className="text-xl font-semibold text-white">Privacy & Cookie Preferences</h3>
                    <p className="text-white/70 text-sm mt-1">BotBlender Trading Dashboard</p>
                  </div>
                </div>
                <button
                  onClick={() => setShowDetails(!showDetails)}
                  className="p-2 hover:bg-white/10 rounded-lg transition-colors"
                  aria-label="Toggle details"
                >
                  <CogIcon className="h-5 w-5 text-white/70" />
                </button>
              </div>

              {/* Main message */}
              <div className="mb-6">
                <p className="text-white/90 leading-relaxed">
                  We use cookies to enhance your trading experience, provide personalized insights, and improve our services. 
                  You can customize your preferences or accept all cookies to continue.
                </p>
              </div>

              {/* Detailed options */}
              <AnimatePresence>
                {showDetails && (
                  <motion.div
                    initial={{ height: 0, opacity: 0 }}
                    animate={{ height: "auto", opacity: 1 }}
                    exit={{ height: 0, opacity: 0 }}
                    transition={{ duration: 0.3 }}
                    className="overflow-hidden"
                  >
                    <div className="space-y-4 mb-6 bg-black/20 rounded-xl p-6">
                      <h4 className="text-white font-medium mb-4">Customize Your Cookie Preferences</h4>
                      
                      {/* Essential Cookies */}
                      <div className="flex items-center justify-between p-4 bg-white/5 rounded-lg">
                        <div className="flex-1">
                          <h5 className="text-white font-medium">Essential Cookies</h5>
                          <p className="text-white/60 text-sm mt-1">
                            Required for basic functionality, authentication, and security
                          </p>
                        </div>
                        <div className="ml-4">
                          <div className="px-3 py-1 bg-green-500/20 text-green-400 text-xs font-medium rounded-full">
                            Always Active
                          </div>
                        </div>
                      </div>

                      {/* Analytics Cookies */}
                      <div className="flex items-center justify-between p-4 bg-white/5 rounded-lg">
                        <div className="flex-1">
                          <h5 className="text-white font-medium">Analytics & Performance</h5>
                          <p className="text-white/60 text-sm mt-1">
                            Help us understand usage patterns and improve app performance
                          </p>
                        </div>
                        <div className="ml-4">
                          <button
                            onClick={() => togglePreference('analytics')}
                            className={`relative inline-flex h-6 w-11 items-center rounded-full transition-colors ${
                              preferences.analytics ? 'bg-blue-600' : 'bg-white/20'
                            }`}
                          >
                            <span
                              className={`inline-block h-4 w-4 transform rounded-full bg-white transition-transform ${
                                preferences.analytics ? 'translate-x-6' : 'translate-x-1'
                              }`}
                            />
                          </button>
                        </div>
                      </div>

                      {/* Marketing Cookies */}
                      <div className="flex items-center justify-between p-4 bg-white/5 rounded-lg">
                        <div className="flex-1">
                          <h5 className="text-white font-medium">Marketing & Communication</h5>
                          <p className="text-white/60 text-sm mt-1">
                            Enable personalized content and relevant feature notifications
                          </p>
                        </div>
                        <div className="ml-4">
                          <button
                            onClick={() => togglePreference('marketing')}
                            className={`relative inline-flex h-6 w-11 items-center rounded-full transition-colors ${
                              preferences.marketing ? 'bg-blue-600' : 'bg-white/20'
                            }`}
                          >
                            <span
                              className={`inline-block h-4 w-4 transform rounded-full bg-white transition-transform ${
                                preferences.marketing ? 'translate-x-6' : 'translate-x-1'
                              }`}
                            />
                          </button>
                        </div>
                      </div>

                      {/* Personalization Cookies */}
                      <div className="flex items-center justify-between p-4 bg-white/5 rounded-lg">
                        <div className="flex-1">
                          <h5 className="text-white font-medium">Personalization & Features</h5>
                          <p className="text-white/60 text-sm mt-1">
                            Customize your experience and enable advanced trading features
                          </p>
                        </div>
                        <div className="ml-4">
                          <button
                            onClick={() => togglePreference('personalization')}
                            className={`relative inline-flex h-6 w-11 items-center rounded-full transition-colors ${
                              preferences.personalization ? 'bg-blue-600' : 'bg-white/20'
                            }`}
                          >
                            <span
                              className={`inline-block h-4 w-4 transform rounded-full bg-white transition-transform ${
                                preferences.personalization ? 'translate-x-6' : 'translate-x-1'
                              }`}
                            />
                          </button>
                        </div>
                      </div>
                    </div>
                  </motion.div>
                )}
              </AnimatePresence>

              {/* Action buttons */}
              <div className="flex flex-col md:flex-row gap-3">
                {showDetails ? (
                  <>
                    <button
                      onClick={handleSavePreferences}
                      disabled={isLoading}
                      className="flex-1 bg-blue-600 hover:bg-blue-700 disabled:opacity-50 disabled:cursor-not-allowed text-white font-medium py-3 px-6 rounded-lg transition-colors"
                    >
                      {isLoading ? 'Saving...' : 'Save My Preferences'}
                    </button>
                    <button
                      onClick={() => setShowDetails(false)}
                      className="md:w-auto px-6 py-3 text-white/70 hover:text-white hover:bg-white/10 rounded-lg transition-colors"
                    >
                      Back
                    </button>
                  </>
                ) : (
                  <>
                    <button
                      onClick={handleAcceptAll}
                      disabled={isLoading}
                      className="flex-1 md:flex-none bg-blue-600 hover:bg-blue-700 disabled:opacity-50 disabled:cursor-not-allowed text-white font-medium py-3 px-8 rounded-lg transition-colors"
                    >
                      {isLoading ? 'Processing...' : 'Accept All'}
                    </button>
                    <button
                      onClick={() => setShowDetails(true)}
                      className="flex-1 md:flex-none border border-white/20 hover:bg-white/10 text-white font-medium py-3 px-8 rounded-lg transition-colors"
                    >
                      Customize
                    </button>
                    <button
                      onClick={handleRejectAll}
                      disabled={isLoading}
                      className="md:w-auto text-white/70 hover:text-white py-3 px-4 transition-colors"
                    >
                      Reject All
                    </button>
                  </>
                )}
              </div>

              {/* Privacy policy link */}
              <div className="mt-4 pt-4 border-t border-white/10">
                <p className="text-white/60 text-xs text-center">
                  By continuing to use our service, you agree to our{' '}
                  <a href="/privacy-policy" className="text-blue-400 hover:text-blue-300 underline">
                    Privacy Policy
                  </a>{' '}
                  and{' '}
                  <a href="/cookie-policy" className="text-blue-400 hover:text-blue-300 underline">
                    Cookie Policy
                  </a>
                  . You can change your preferences anytime in Settings.
                </p>
              </div>
            </div>
          </div>
        </div>
      </motion.div>
    </AnimatePresence>
  );
};